import {
    Cpf,
    isInteger as isIntegerCheck,
    removeNonDigits,
} from '@superbet-group/web.lib.utilities';
import { ValidationErrorTranslationKey } from '@superbet-group/web.types';

export function checkIbanFormat(ibanFormateRegex: string): (value?: string) => boolean | ValidationErrorTranslationKey {
    const regex = new RegExp(ibanFormateRegex);

    return (value?: string): boolean | ValidationErrorTranslationKey => {
        if (!value || regex.test(value)) {
            return false;
        }
        return { translationKey: 'pam.withdrawal.addAccountNumber.invalidIbanFormat' };
    };
}

/**
 * It checks all files but returns error for the first one (app don't have multiple files uploads for now)
 */
export function maxFileSize(maxSize: number, maxSizeHumanReadable: string): (files?: FileList) => boolean | ValidationErrorTranslationKey {
    return (files?: FileList): boolean | ValidationErrorTranslationKey => {
        if (!files || files.length === 0) {
            return false;
        }

        const hasInvalid = Array.from(files).some((file) => maxSize < file.size);
        return !hasInvalid ? false : { translationKey: 'maximum file size is {value}', payload: { value: maxSizeHumanReadable } };
    };
}

/**
 * Very liberal format checking.  If we want additional restrictions we can check it with FileReader.
 * It checks all files but returns error for the first one (app don't have multiple files uploads for now)
 */
export function allowedFormatsByFilename(allowedFormats: string[]): (files?: FileList) => boolean | ValidationErrorTranslationKey {
    return (files?: FileList): boolean | ValidationErrorTranslationKey => {
        if (!files || files.length === 0) {
            return false;
        }

        const hasInvalid = Array.from(files).some((file) => !allowedFormats.includes(file.type));
        const extensions = allowedFormats.map((e) => e.split('/')[1]).join(', ');

        return !hasInvalid
            ? false
            : { translationKey: 'invalid file type (allowed: {extensions})', payload: { extensions } };
    };
}

export function min(minValue: number): (value: number | null | undefined) => boolean | ValidationErrorTranslationKey {
    return (value: number | null | undefined): boolean | ValidationErrorTranslationKey => {
        if (value === null || value === undefined) {
            return false;
        }

        return minValue <= value ? false : { translationKey: 'minimum is {value}', payload: { value: minValue } };
    };
}

export function max(maxValue: number): (value: number | null | undefined) => boolean | ValidationErrorTranslationKey {
    return (value: number | null | undefined): boolean | ValidationErrorTranslationKey => {
        if (value === null || value === undefined) {
            return false;
        }

        return value <= maxValue ? false : { translationKey: 'maximum is {value}', payload: { value: maxValue } };
    };
}

export function isInteger(value: string): boolean | ValidationErrorTranslationKey {
    return isIntegerCheck(value) ? false : { translationKey: 'pam.registration.integerOnly' };
}

export function isValidBrasilPostalCode(value: string): boolean | ValidationErrorTranslationKey {
    const postalCode = removeNonDigits(value); // Remove all non-digit
    return postalCode.length === 8 ? false : { translationKey: 'pam.registration.invalidPostalCode' };
}

export function isCpfValid(value: string): boolean | ValidationErrorTranslationKey {
    const cpf = Cpf.parse(value);

    if (!cpf) {
        return {
            translationKey: 'pam.registration.invalidCpf',
        };
    }

    return false;
}

export function isPassportValid(value: string): boolean | ValidationErrorTranslationKey {
    const prefix = value.slice(0, 2);
    const numbers = value.slice(2);

    const startIsInvalid = !(/^[a-zA-Z]+$/.test(prefix)) || !(/^[\d]+$/.test(numbers));

    if (!startIsInvalid) {
        return false;
    }

    return { translationKey: 'pam.registration.passportNumber.incorrect' };
}
